/**
 * Reading what was actually dropped.
 *
 * `dataTransfer.files` is what every dropzone tutorial reaches for and it is
 * wrong for anything but a flat set of files: **dropping a folder yields nothing
 * there.** The design's dropzone promises "drop files or folders", and until now
 * dropping a folder hit `if (e.dataTransfer.files.length)` and silently did
 * nothing — the single most confusing failure a dropzone can have, because the
 * cursor said yes and the app did nothing.
 *
 * Folders live behind `dataTransfer.items[i].webkitGetAsEntry()`, a non-standard
 * API that every browser we support implements and none has replaced. The
 * standard successor (`getAsFileSystemHandle`) is Chromium-only, so it would
 * mean maintaining both paths to serve fewer people.
 *
 * ── Why this is its own module ──────────────────────────────────────────────
 * Two reasons, and the second is the load-bearing one:
 *
 *  1. It is a pure function of a DataTransfer, so it can be tested under vitest
 *     in the node environment with a hand-built fake — the way
 *     `crypto/stream.test.ts` tests `planUpload`. There is no jsdom in this
 *     repo and adding one to cover a dropzone would be a poor trade.
 *  2. **A DataTransfer is only valid during the drop event.** Read it in the
 *     handler, synchronously, or it comes back empty — which looks exactly like
 *     an empty folder rather than a bug. Isolating the read makes that rule easy
 *     to state and hard to break by adding an `await` above it.
 */

/** One file to upload, with where it should land relative to the drop. */
export interface DroppedFile {
  file: File;
  /**
   * Folder path relative to the current folder, e.g. `['Course assets', 'img']`.
   * Empty for a file dropped on its own — it lands in the folder being viewed,
   * not in one named after itself.
   */
  path: string[];
}

/**
 * A drop of a home directory is a mistake, not a request. The cap is high
 * enough that no deliberate drop hits it and low enough that a mis-drop does
 * not walk a million entries before anyone can cancel.
 */
export const MAX_DROPPED_FILES = 10_000;

/**
 * Directory depth to descend. A cycle cannot happen through this API — entries
 * are a tree, not a filesystem with links — but a pathological archive can be
 * arbitrarily deep, and unbounded recursion here is an unbounded stack.
 */
export const MAX_DEPTH = 32;

/** Minimal shapes of the non-standard entry API, so this file needs no DOM lib. */
interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}
interface FsFileEntry extends FsEntry {
  file(onSuccess: (file: File) => void, onError?: (err: unknown) => void): void;
}
interface FsDirectoryEntry extends FsEntry {
  createReader(): {
    readEntries(onSuccess: (entries: FsEntry[]) => void, onError?: (err: unknown) => void): void;
  };
}

function toFile(entry: FsFileEntry): Promise<File | null> {
  // Resolves rather than rejects on failure: one unreadable file in a dropped
  // folder should cost that file, not the whole drop.
  return new Promise((resolve) => entry.file(resolve, () => resolve(null)));
}

/**
 * Every entry in a directory.
 *
 * `readEntries` returns them **in batches** and signals the end with an empty
 * batch — it does not return the whole listing. Reading once and stopping is the
 * classic bug here: Chrome caps a batch at 100, so a folder of 250 files
 * uploads 100 of them and reports success.
 */
async function readAll(dir: FsDirectoryEntry): Promise<FsEntry[]> {
  const reader = dir.createReader();
  const out: FsEntry[] = [];
  for (;;) {
    const batch = await new Promise<FsEntry[]>((resolve) =>
      reader.readEntries(resolve, () => resolve([]))
    );
    if (!batch.length) return out;
    out.push(...batch);
  }
}

async function walk(
  entry: FsEntry,
  path: string[],
  out: DroppedFile[],
  depth: number
): Promise<void> {
  if (out.length >= MAX_DROPPED_FILES) return;

  if (entry.isFile) {
    const file = await toFile(entry as FsFileEntry);
    if (file) out.push({ file, path });
    return;
  }

  if (!entry.isDirectory || depth >= MAX_DEPTH) return;

  // The directory's own name joins the path, so dropping "Course assets" makes
  // "Course assets/logo.png" rather than scattering its contents into the
  // folder being viewed.
  const inner = [...path, entry.name];
  for (const child of await readAll(entry as FsDirectoryEntry)) {
    await walk(child, inner, out, depth + 1);
    if (out.length >= MAX_DROPPED_FILES) return;
  }
}

/**
 * Everything dropped, flattened, with folder structure preserved as `path`.
 *
 * **Call this synchronously from the drop handler.** It reads `items` up front —
 * before its first `await` — because a DataTransfer is dead the moment the
 * handler returns, and a read after that yields an empty list.
 */
export async function readDrop(dataTransfer: DataTransfer): Promise<DroppedFile[]> {
  const items = Array.from(dataTransfer.items ?? []);

  // Snapshot before awaiting anything. This line is the whole reason the
  // function is shaped this way.
  const entries = items
    .filter((item) => item.kind === 'file')
    .map((item) =>
      typeof (item as { webkitGetAsEntry?: unknown }).webkitGetAsEntry === 'function'
        ? (item.webkitGetAsEntry() as FsEntry | null)
        : null
    );

  // No entry API at all: fall back to the flat file list, which is what the old
  // handler did and is still right for a browser that cannot describe folders.
  if (entries.every((e) => e === null)) {
    return Array.from(dataTransfer.files ?? []).map((file) => ({ file, path: [] }));
  }

  const out: DroppedFile[] = [];
  for (const entry of entries) {
    if (!entry) continue;
    await walk(entry, [], out, 0);
    if (out.length >= MAX_DROPPED_FILES) break;
  }
  return out;
}

/**
 * Does this drag carry files at all?
 *
 * Dragging a link, a selection of text or an image out of another page produces
 * a DataTransfer with no files behind it. Accepting one shows the drop overlay
 * and then does nothing, which reads as broken — so the overlay is gated on this
 * rather than on `hasUrls`-style optimism.
 *
 * `types` is the only part of a DataTransfer readable during `dragover`; `items`
 * and `files` are deliberately empty until the drop, for privacy.
 */
/**
 * What `<input type="file" webkitdirectory>` hands back, in the shape `upload`
 * already consumes.
 *
 * The picker gives a flat `FileList` where each file carries its path in
 * `webkitRelativePath` — `Course assets/img/hero.png` — with the picked folder
 * as the first segment. Keeping that first segment is deliberate: choosing a
 * folder should produce a folder, which is what every other file manager does
 * and what a drop of the same folder already does here.
 *
 * The last segment is the filename and is dropped: `path` is the folders to
 * create, not where the file ends up. Files with no relative path at all —
 * which a browser is permitted to return — land in the current folder rather
 * than being skipped.
 */
export function fromDirectoryPicker(files: FileList | File[]): DroppedFile[] {
  return Array.from(files).map((file) => {
    const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    const segments = relative.split('/').filter(Boolean);
    return { file, path: segments.slice(0, -1) };
  });
}

export function dragHasFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) return false;
  return Array.from(dataTransfer.types ?? []).includes('Files');
}
